import { ChevronRightSharp, DescriptionOutlined, ExpandMoreSharp } from '@mui/icons-material';
import {
  Button,
  Checkbox,
  CircularProgress,
  Container,
  styled,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { usePrevious } from '../../hooks/usePrevious';
import { selectFolders } from '../../redux/reducers/folderReducer';
import { rescanQuestions, updateProjectQuestionRunInclusion } from '../../services/dealGPT';
import {
  StyledTableCell,
  StyledTableHeaderCell,
  StyledTableRow,
} from '../../theme/CustomComponents';
import type { QuestionTypeTreeNode } from '../../Types/dealGPT';
import { DealGPTQuestionStatus, IndexType } from '../../Types/enums';
import { SourceDocument } from '../../Types/indexes';
import { AnalyticsEvent, useAnalytics } from '../Providers/AnalyticsProvider';
import { useDrawer } from '../Providers/DrawerProvider';
import QuestionStatus from './QuestionStatus';
import QuestionView from './QuestionView';

const StyledButton = styled(Button)({
  width: '200px',
  height: '35px',
});

const CategoryViewContainer = styled(Container)({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  margin: '40px auto',
});

const StyledTypography = styled(Typography)({
  display: 'block',
  fontSize: '0.875rem',
});

const SourceDocumentsContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'end',
  gap: '6px',
  color: '#1979CD',
});

const SourceDocumentsTypography = styled(StyledTypography)({
  width: '130px',
});

export type CategoryViewProps = {
  questions: QuestionTypeTreeNode[];
  expandAll: boolean;
  indexToUse: IndexType;
  indexId: string;
  projectId: string;
  runId: string;
  setQuestions: (nodes: QuestionTypeTreeNode[]) => void;
  setShouldFetchRunProgress: (isRescan: boolean) => void;
  isLoadingProjectRescan: boolean;
};

const CategoryView = ({
  questions,
  expandAll,
  indexToUse,
  indexId,
  projectId,
  runId,
  setQuestions,
  setShouldFetchRunProgress,
  isLoadingProjectRescan,
}: CategoryViewProps): JSX.Element => {
  const prevExpandAllRef = usePrevious(expandAll);
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());
  const [isLoadingRescan, setIsLoadingRescan] = useState<boolean>(false);
  const [currentRescanQuestions, setCurrentRescanQuestions] = useState<QuestionTypeTreeNode[]>([]);
  const { currentFolder } = useSelector(selectFolders);
  const { openDrawer } = useDrawer();
  const analytics = useAnalytics();

  const flattenQuestionTreeNodes = useCallback(
    (arr: QuestionTypeTreeNode[]): QuestionTypeTreeNode[] => {
      return arr.reduce((acc: QuestionTypeTreeNode[], item: QuestionTypeTreeNode) => {
        acc.push(item);
        if (Array.isArray(item['children'])) {
          return acc.concat(flattenQuestionTreeNodes(item['children']));
        }
        return acc;
      }, []);
    },
    []
  );

  const flattendQuestions = useMemo(() => {
    return flattenQuestionTreeNodes(questions);
  }, [questions, flattenQuestionTreeNodes]);

  const allQuestionIds = useMemo(() => {
    const ids = new Set<string>();
    const collectAllQuestionIds = (question: QuestionTypeTreeNode) => {
      ids.add(question.id);
      question.children?.forEach(collectAllQuestionIds);
    };
    questions.forEach(collectAllQuestionIds);
    return ids;
  }, [questions]);

  useEffect(() => {
    if (prevExpandAllRef !== expandAll || expandAll) {
      expandAll ? setExpandedQuestions(new Set(allQuestionIds)) : setExpandedQuestions(new Set());
    }
  }, [expandAll, prevExpandAllRef, allQuestionIds]);

  const handleOpenQuestion = (questionId: string) => {
    const question = flattendQuestions.find((q) => q.id === questionId);

    if (!question) return;

    analytics.event(
      AnalyticsEvent.WM_IA_D_QUESTION_CLICK,
      {
        id: questionId,
      },
      {
        projectId,
        runId,
        scope: question.scope,
        categoryName: question.category,
        questionId: question.id,
      }
    );

    openDrawer({
      anchor: 'right',
      header: question.question,
      content: (
        <QuestionView
          projectId={projectId}
          runId={runId}
          setQuestions={setQuestions}
          questions={flattendQuestions}
          questionsTree={questions}
          question={question}
        />
      ),
    });
  };

  const toggleExpandQuestion = (question: QuestionTypeTreeNode) => {
    let isExpanded = false;
    setExpandedQuestions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(question.id)) {
        newSet.delete(question.id);
      } else {
        isExpanded = true;
        newSet.add(question.id);
      }
      return newSet;
    });
    analytics.event(
      AnalyticsEvent.WM_IA_D_QUESTION_EXPAND,
      {
        id: question.id,
        value: isExpanded.toString(),
      },
      {
        projectId,
        runId,
        scope: question.scope,
        categoryName: question.category,
        questionId: question.id,
        numChildQuestions: question.children.length,
      }
    );
  };

  const handleRescanQuestion = async (questionId?: string) => {
    if (!currentFolder?.id) {
      enqueueSnackbar('No folder selected. Please ensure a folder is selected.', {
        variant: 'error',
      });
      return;
    }

    try {
      let analyticsRescanValue = `Category=${questions[0].category},Questions=`;
      let flattendCategoryQuestions = flattendQuestions;

      // if questionId is present rescan only that question and its children, if not, then rescan all category questions.
      if (questionId) {
        analyticsRescanValue = '';
        const questionObject = flattendCategoryQuestions.find((q) => q.id === questionId);
        if (questionObject) {
          flattendCategoryQuestions = flattenQuestionTreeNodes([questionObject]);
        } else {
          throw new Error('Question not found.');
        }
      }
      setCurrentRescanQuestions(flattendCategoryQuestions);

      flattendCategoryQuestions = flattendCategoryQuestions.map((question) => ({
        ...question,
        children: [],
        response: undefined,
        explanation: undefined,
        apiResponse: undefined,
        documents: [],
        validationResponse: null,
        validationApiResponse: null,
        validationSources: [],
        projectQuestionFeedback: null,
      }));

      setIsLoadingRescan(true);
      const questionIds = flattendCategoryQuestions.map((question) => question.id);
      analytics.event(
        AnalyticsEvent.WM_IA_D_RESCAN_QUESTIONS,
        {
          value: analyticsRescanValue + questionIds.toString(),
        },
        {
          projectId,
          runId,
          scope: questions[0].scope,
          categoryName: questions[0].category,
          questionIds,
        }
      );

      await rescanQuestions({
        questions: flattendCategoryQuestions,
        indexToUse,
        indexId,
        folderId: currentFolder.id,
      });
      enqueueSnackbar(`Rescan started successfully`, { variant: 'info' });
      setShouldFetchRunProgress(true);
    } catch {
      enqueueSnackbar('Failed to Rescan project questions.', { variant: 'error' });
    }
    setIsLoadingRescan(false);
  };

  const handleRunInclusionChanged = async (questionId: string, includeInRun: boolean) => {
    try {
      await updateProjectQuestionRunInclusion(questionId, includeInRun);
      setQuestions(questions.map((q) => (q.id === questionId ? { ...q, includeInRun } : q)));
    } catch {
      enqueueSnackbar('Failed to update question run inclusion.', { variant: 'error' });
    }
  };

  const handleRunInclusionChangedForAllQuestions = async (includeInRun: boolean) => {
    try {
      await Promise.all(
        questions.map((question) => updateProjectQuestionRunInclusion(question.id, includeInRun))
      );
      setQuestions(questions.map((q) => ({ ...q, includeInRun })));
    } catch (error) {
      enqueueSnackbar(`${error}`, { variant: 'error' });
    }
  };

  const sourceDocumentContainer = (
    sources: SourceDocument[] | undefined,
    status: DealGPTQuestionStatus
  ) => {
    if (!sources || sources.length === 0 || status === DealGPTQuestionStatus.NOT_FOUND) return;

    if (sources[0].openaiId) {
      // For openai files, multiple chunks can be returned that are the same documents
      sources = sources.filter(
        (source, index, self) => index === self.findIndex((t) => t.openaiId === source.openaiId)
      );
    }

    const sourcesContainer = (fileName?: string | undefined) => {
      return (
        <SourceDocumentsContainer>
          <DescriptionOutlined sx={{ fontSize: '16px', color: '#00a3ff' }} />
          <SourceDocumentsTypography noWrap>
            {fileName ? fileName : `${sources?.length} documents`}
          </SourceDocumentsTypography>
        </SourceDocumentsContainer>
      );
    };

    return sources.length === 1 ? (
      <a
        href={sources[0].link}
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration: 'none' }}
      >
        {sourcesContainer(sources[0].fileName)}
      </a>
    ) : (
      sourcesContainer()
    );
  };

  const generateHierarchicalQuestions = (
    question: QuestionTypeTreeNode,
    level = 0
  ): ReactElement[] => {
    const isExpanded = expandedQuestions.has(question.id);
    const questionStatuses: string[] = [];

    questionStatuses.push(question.status);

    if (question.validationResponse) {
      questionStatuses.push('VALIDATED');
    }
    if (question.aggregatedRelevantKeyQuestions.length > 0) {
      questionStatuses.push('RELATED');
    }

    const questionElement = (
      <StyledTableRow
        key={question.id}
        onClick={() => handleOpenQuestion(question.id)}
        sx={{ cursor: 'pointer' }}
      >
        <StyledTableCell align="center">
          <Tooltip
            title={
              question.includeInRun
                ? 'This question is going to be submitted to the LLM model.'
                : 'This question is not going to be submitted to the LLM model.'
            }
            arrow
          >
            <Checkbox
              checked={question.includeInRun}
              onChange={(_, included) => handleRunInclusionChanged(question.id, included)}
              onClick={(e) => e.stopPropagation()}
            />
          </Tooltip>
        </StyledTableCell>
        <StyledTableCell
          sx={{ minWidth: '250px', alignItems: 'center', pl: `${level * 40 + 12}px` }}
          scope="row"
        >
          <div style={{ display: 'flex' }}>
            {question.children && question.children.length > 0 && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpandQuestion(question);
                }}
              >
                {isExpanded ? <ExpandMoreSharp /> : <ChevronRightSharp />}
              </div>
            )}

            {question.question}
          </div>
        </StyledTableCell>
        <StyledTableCell sx={{ width: '100px' }}>
          {question.validationSources
            ? sourceDocumentContainer(question.validationSources, question.status)
            : sourceDocumentContainer(question.documents, question.status)}
        </StyledTableCell>
        <StyledTableCell align="right" sx={{ minWidth: '100px' }}>
          <QuestionStatus statuses={questionStatuses} />
        </StyledTableCell>
        <StyledTableCell align="center">
          {question.status !== DealGPTQuestionStatus.SKIPPED ? (
            isLoadingRescan && currentRescanQuestions.includes(question) ? (
              <CircularProgress size={30} />
            ) : (
              <Button
                disabled={isLoadingRescan || isLoadingProjectRescan}
                variant="contained"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRescanQuestion(question.id);
                }}
              >
                Rescan
              </Button>
            )
          ) : (
            <></>
          )}
        </StyledTableCell>
      </StyledTableRow>
    );

    const childElements = isExpanded
      ? question.children?.flatMap((childQuestion) =>
          generateHierarchicalQuestions(childQuestion, level + 1)
        ) || []
      : [];

    return [questionElement, ...childElements];
  };

  const includedQuestions = useMemo(() => questions.filter((q) => q.includeInRun), [questions]);

  return (
    <CategoryViewContainer maxWidth="lg">
      <StyledButton
        disabled={isLoadingRescan || isLoadingProjectRescan}
        variant="contained"
        onClick={() => handleRescanQuestion()}
      >
        Rescan Category
      </StyledButton>
      <TableContainer
        sx={{
          overflowX: 'auto',
          maxWidth: '1200px',
        }}
      >
        <Table
          id={'category-questions-container'}
          stickyHeader
          size="small"
          sx={{ border: '1px solid rgb(244, 244, 244)' }}
        >
          <TableHead>
            <TableRow sx={{ th: { borderBottom: '2px solid rgb(244, 244, 244)' } }}>
              <StyledTableHeaderCell align="center">
                <Tooltip
                  title={
                    includedQuestions.length === 0
                      ? 'No questions are going to be submitted to the LLM model.'
                      : includedQuestions.length === questions.length
                      ? 'All questions are going to be submitted to the LLM model.'
                      : `Only ${includedQuestions.length} question${
                          includedQuestions.length > 1 ? 's are' : ' is'
                        } going to be submitted to the LLM model.`
                  }
                  arrow
                >
                  <Checkbox
                    checked={includedQuestions.length === questions.length}
                    onChange={(_, included) => handleRunInclusionChangedForAllQuestions(included)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Tooltip>
              </StyledTableHeaderCell>
              <StyledTableHeaderCell>Question</StyledTableHeaderCell>
              <StyledTableHeaderCell>Source(s)</StyledTableHeaderCell>
              <StyledTableHeaderCell>Status</StyledTableHeaderCell>
              <StyledTableHeaderCell align="center">Rescan</StyledTableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {questions.flatMap((question) => generateHierarchicalQuestions(question))}
          </TableBody>
        </Table>
      </TableContainer>
    </CategoryViewContainer>
  );
};

export default CategoryView;
